import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import styles from '../AuthenticationFields.css';

/**
 * Microsoft Azure Copilot OAuth 2 Client Credentials Authentication Form
 *
 * Renders authentication fields for Microsoft Azure Copilot using OAuth 2.0:
 * - Tenant ID
 * - Client ID
 * - Client Secret (sensitive - cleared in edit mode)
 * - Scope
 */
const OAuth2Form = ({ mode, formData, onChange }) => {
  const handleTenantIdChange = useCallback(
    (e) => onChange('tenantId', e.value),
    [onChange]
  );

  const handleClientIdChange = useCallback(
    (e) => onChange('clientId', e.value),
    [onChange]
  );

  const handleClientSecretChange = useCallback(
    (e) => onChange('clientSecret', e.value),
    [onChange]
  );

  const handleScopeChange = useCallback(
    (e) => onChange('scope', e.value),
    [onChange]
  );

  return (
    <div
      className={styles['auth-fields']}
      data-test-id="azure-copilot-oauth2-fields"
    >
      <div className={styles.field}>
        <label htmlFor="tenant-id" className={styles.label}>
          Tenant ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="tenant-id"
            name="tenantId"
            value={formData.tenantId || ''}
            onChange={handleTenantIdChange}
            required
            placeholder="Enter tenant ID"
            hint="The Azure Active Directory tenant (directory) ID. Found in Azure Portal under Azure Active Directory > Properties."
            testId="tenant-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="client-id" className={styles.label}>
          Client ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="client-id"
            name="clientId"
            value={formData.clientId || ''}
            onChange={handleClientIdChange}
            required
            placeholder="Enter client ID"
            hint="The Application (client) ID of your Azure AD app registration. Found in Azure Portal under App registrations > Your App > Overview."
            testId="client-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="client-secret" className={styles.label}>
          Client secret{' '}
          {mode === 'add' && <span className={styles.required}>*</span>}
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="client-secret"
            name="clientSecret"
            type="password"
            value={formData.clientSecret || ''}
            onChange={handleClientSecretChange}
            required={mode === 'add'}
            placeholder={
              mode === 'edit'
                ? 'Enter new secret to update (leave empty to keep existing)'
                : 'Enter client secret value'
            }
            hint="The client secret value created for your Azure AD app registration. Found in Azure Portal under App registrations > Your App > Certificates & secrets."
            testId="client-secret-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="scope" className={styles.label}>
          Scope <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="scope"
            name="scope"
            value={formData.scope || ''}
            onChange={handleScopeChange}
            required
            placeholder="https://<orgId>.api.crm.dynamics.com/.default"
            hint="The OAuth 2.0 scope for accessing your Dynamics 365 environment. Format: https://<orgId>.api.crm.dynamics.com/.default"
            testId="scope-input"
          />
        </div>
      </div>
    </div>
  );
};

OAuth2Form.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  formData: PropTypes.shape({
    tenantId: PropTypes.string,
    clientId: PropTypes.string,
    clientSecret: PropTypes.string,
    scope: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

OAuth2Form.defaultProps = {
  mode: 'add'
};

export default OAuth2Form;
